import { useEffect, useRef, type ReactNode } from 'react';
import { IconButton } from './Button';
import { trapTab, useEscape } from './hooks';

/**
 * Khung ngữ cảnh bên phải (thông tin hội thoại, thành viên…). `onClose` hiện nút đóng.
 * `drawer` (màn < 1200px, W1.4): khung phủ lên hội thoại như ngăn kéo — có nền mờ (bấm để đóng), Esc đóng, focus vào
 * nút đóng khi mở, giữ focus trong ngăn kéo (Tab vòng lại, không lọt ra nội dung bị nền mờ che) và trả focus về phần
 * tử đã mở khi đóng (W1.6).
 */
export function ContextPanel({
  title,
  onClose,
  children,
  footer,
  testId,
  drawer = false,
}: {
  title: ReactNode;
  onClose?: () => void;
  children: ReactNode;
  footer?: ReactNode;
  testId?: string;
  drawer?: boolean;
}) {
  const close = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLElement>(null);
  useEscape(drawer && !!onClose, () => onClose?.());

  useEffect(() => {
    if (!drawer) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    close.current?.focus();
    // Trả focus khi đóng ngăn kéo (không khi chuyển sang bố cục rộng — lúc đó khung vẫn hiển thị).
    return () => {
      if (previous?.isConnected && (!document.activeElement || document.activeElement === document.body)) previous.focus();
    };
  }, [drawer]);

  return (
    <>
      {drawer && onClose && <div className="ws-drawer-backdrop" data-testid="drawer-backdrop" onClick={onClose} />}
      <aside
        ref={box}
        className={`ws-context${drawer ? ' is-drawer' : ''}`}
        aria-label="Thông tin hội thoại"
        data-testid={testId}
        onKeyDown={drawer && onClose ? (e) => trapTab(e, box.current) : undefined}
      >
        <header className="ws-context-head">
          <h3>{title}</h3>
          {onClose && <IconButton ref={close} label="Đóng khung thông tin" icon="close" onClick={onClose} />}
        </header>
        <div className="ws-context-scroll">{children}</div>
        {footer && <footer className="ws-context-foot">{footer}</footer>}
      </aside>
    </>
  );
}

/** Một mục trong khung ngữ cảnh: tiêu đề + nội dung, tùy chọn nút hành động cạnh tiêu đề. */
export function ContextSection({ title, action, children, testId }: { title: string; action?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <section className="ws-context-section" data-testid={testId}>
      <h4>
        <span>{title}</span>
        {action}
      </h4>
      {children}
    </section>
  );
}
